import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Zap, Workflow as WorkflowIcon, PlayCircle, XCircle, CheckCircle2,
  Activity, Loader2, Bell, ClipboardList,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * P7 — Automation Dashboard
 * URL: /admin/automation/dashboard
 */

const AutomationDashboard = () => {
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [stats, setStats] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API_URL}/api/automation/dashboard`, authHeader),
        axios.get(`${API_URL}/api/automation/executions?limit=12`, authHeader),
      ]);
      setStats(s.data);
      setExecutions(e.data || []);
    } catch (_) { /* no-op */ }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, [load]);

  if (loading || !stats) {
    return <div className="p-6 flex items-center gap-2 text-slate-500" data-testid="automation-dashboard-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading dashboard…</div>;
  }

  return (
    <div data-testid="automation-dashboard-page">
      <div className="mb-5 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
          <Zap className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42]">Automation Dashboard</h1>
          <p className="text-sm text-[#6C757D]">Workflow executions, tasks and notifications in the last 24 hours.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6" data-testid="automation-kpi-grid">
        <KPI label="Workflows active" value={`${stats.workflows_active}/${stats.workflows_total}`} icon={WorkflowIcon} color="bg-violet-500" testId="kpi-workflows" />
        <KPI label="Runs 24h" value={stats.runs_24h} icon={PlayCircle} color="bg-sky-500" testId="kpi-runs" />
        <KPI label="Failures 24h" value={stats.failures_24h} icon={XCircle} color="bg-rose-500" testId="kpi-failures" />
        <KPI label="Tasks open" value={stats.tasks_open} icon={ClipboardList} color="bg-amber-500" testId="kpi-tasks" />
        <KPI label="Notifs 24h" value={stats.notifications_sent_24h} icon={Bell} color="bg-cyan-500" testId="kpi-notifs" />
        <KPI label="Top event" value={stats.top_events?.[0]?.event || '—'} icon={Activity} color="bg-emerald-500" testId="kpi-top-event" small />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card data-testid="automation-executions-card">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42] mb-3">Recent Executions</h3>
            {executions.length === 0 ? (
              <p className="text-xs text-[#6C757D]">No workflow executions yet. Emit a test event to see them here.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {executions.map((e) => (
                  <li key={e.id} className="py-2 flex items-start gap-2" data-testid={`automation-execution-${e.id}`}>
                    {e.status === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                     : e.status === 'failure' ? <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                     : <Activity className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#2B2D42] truncate">{e.workflow_name}</div>
                      <div className="text-[11px] text-[#6C757D]">{e.event} · {(e.triggered_at || '').slice(0, 19).replace('T', ' ')} · {e.actions_log?.length || 0} actions</div>
                    </div>
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                      e.status === 'success' ? 'bg-emerald-100 text-emerald-700'
                      : e.status === 'failure' ? 'bg-rose-100 text-rose-700'
                      : 'bg-amber-100 text-amber-700'
                    }`}>{e.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card data-testid="automation-top-card">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42] mb-3">Top Workflows (24h)</h3>
            {(stats.top_workflows || []).length === 0 ? (
              <p className="text-xs text-[#6C757D]">No executions in the last 24 hours.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {stats.top_workflows.map((w) => (
                  <li key={w.workflow_id} className="py-2 flex items-center justify-between" data-testid={`automation-top-workflow-${w.workflow_id}`}>
                    <span className="text-sm text-[#2B2D42] truncate">{w.name}</span>
                    <span className="text-xs font-mono font-bold text-[#FF6B35]">{w.count}</span>
                  </li>
                ))}
              </ul>
            )}
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#2B2D42] mt-5 mb-3">Top Events (24h)</h3>
            {(stats.top_events || []).length === 0 ? (
              <p className="text-xs text-[#6C757D]">No events emitted in the last 24 hours.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {stats.top_events.map((e) => (
                  <li key={e.event} className="py-2 flex items-center justify-between" data-testid={`automation-top-event-${e.event}`}>
                    <code className="text-xs text-[#023E8A]">{e.event}</code>
                    <span className="text-xs font-mono font-bold text-[#FF6B35]">{e.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};


const KPI = ({ label, value, icon: Icon, color, testId, small }) => (
  <Card data-testid={testId}>
    <CardContent className="p-3">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[10px] text-[#6C757D] uppercase font-semibold mb-0.5">{label}</p>
          <p className={`${small ? 'text-sm' : 'text-2xl'} font-bold text-[#2B2D42] truncate`}>{value}</p>
        </div>
        <div className={`${color} p-2 rounded-lg flex-shrink-0`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);


export default AutomationDashboard;
